import React, { useEffect, useMemo, useRef, useState } from 'react';

const ASPECT_OPTIONS = [
  { key: 'free', label: 'Free', ratio: null },
  { key: '1:1', label: '1:1', ratio: 1 },
  { key: '16:9', label: '16:9', ratio: 16 / 9 },
  { key: '4:3', label: '4:3', ratio: 4 / 3 },
];

function clampOffset(offset, maxOverflow) {
  if (maxOverflow <= 0) {
    return 0;
  }

  return Math.min(maxOverflow / 2, Math.max(-maxOverflow / 2, offset));
}

function ImageCropModal({ image, onClose, onConfirm }) {
  const viewportRef = useRef(null);
  const pointerStartRef = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 1, height: 1 });
  const [viewportSize, setViewportSize] = useState({ width: 520, height: 320 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [aspectKey, setAspectKey] = useState('free');

  const activeAspect = useMemo(
    () => ASPECT_OPTIONS.find((option) => option.key === aspectKey) ?? ASPECT_OPTIONS[0],
    [aspectKey]
  );

  useEffect(() => {
    const previewImage = new Image();
    previewImage.onload = () => {
      setNaturalSize({
        width: previewImage.naturalWidth || 1,
        height: previewImage.naturalHeight || 1,
      });
    };
    previewImage.src = image.src;
  }, [image.src]);

  useEffect(() => {
    const updateViewportSize = () => {
      const node = viewportRef.current;
      if (!node) return;

      const bounds = node.getBoundingClientRect();
      setViewportSize({
        width: bounds.width || 520,
        height: bounds.height || 320,
      });
    };

    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);

    return () => window.removeEventListener('resize', updateViewportSize);
  }, [aspectKey]);

  const scaleToViewport = useMemo(
    () =>
      Math.max(viewportSize.width / naturalSize.width, viewportSize.height / naturalSize.height) *
      zoom,
    [naturalSize.height, naturalSize.width, viewportSize.height, viewportSize.width, zoom]
  );

  const renderedSize = useMemo(
    () => ({
      width: naturalSize.width * scaleToViewport,
      height: naturalSize.height * scaleToViewport,
    }),
    [naturalSize.height, naturalSize.width, scaleToViewport]
  );

  const constrainedOffset = useMemo(
    () => ({
      x: clampOffset(offset.x, Math.max(0, renderedSize.width - viewportSize.width)),
      y: clampOffset(offset.y, Math.max(0, renderedSize.height - viewportSize.height)),
    }),
    [offset.x, offset.y, renderedSize.height, renderedSize.width, viewportSize.height, viewportSize.width]
  );

  useEffect(() => {
    setOffset(constrainedOffset);
  }, [constrainedOffset]);

  const handlePointerDown = (event) => {
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      offsetX: constrainedOffset.x,
      offsetY: constrainedOffset.y,
    };

    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePointerMove = (event) => {
    if (!pointerStartRef.current) return;

    const deltaX = event.clientX - pointerStartRef.current.x;
    const deltaY = event.clientY - pointerStartRef.current.y;

    setOffset({
      x: pointerStartRef.current.offsetX + deltaX,
      y: pointerStartRef.current.offsetY + deltaY,
    });
  };

  const handlePointerUp = (event) => {
    pointerStartRef.current = null;

    if (event.currentTarget.releasePointerCapture?.(event.pointerId)) {
      // noop
    }
  };

  const handleConfirm = () => {
    const imageElement = new Image();
    imageElement.onload = () => {
      const left = (viewportSize.width - renderedSize.width) / 2 + constrainedOffset.x;
      const top = (viewportSize.height - renderedSize.height) / 2 + constrainedOffset.y;
      const sourceX = Math.max(0, -left / scaleToViewport);
      const sourceY = Math.max(0, -top / scaleToViewport);
      const sourceWidth = Math.min(
        imageElement.naturalWidth - sourceX,
        viewportSize.width / scaleToViewport
      );
      const sourceHeight = Math.min(
        imageElement.naturalHeight - sourceY,
        viewportSize.height / scaleToViewport
      );

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(sourceWidth));
      canvas.height = Math.max(1, Math.round(sourceHeight));

      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }

      context.drawImage(
        imageElement,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height
      );

      onConfirm({
        ...image,
        src: canvas.toDataURL('image/png'),
        previewSrc: null,
        driveFileId: null,
        storageProvider: null,
        type: 'image/png',
        width: canvas.width,
        height: canvas.height,
        name: `${image.name.replace(/\.[^.]+$/, '')}-cropped.png`,
      });
    };

    imageElement.src = image.src;
  };

  const aspectRatio = activeAspect.ratio || naturalSize.width / naturalSize.height || 1;

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal cropper-modal">
        <div className="modal-header">
          <h2 className="modal-title">Crop Image</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="cropper-toolbar" role="toolbar" aria-label="Crop aspect ratio presets">
          {ASPECT_OPTIONS.map((option) => (
            <button
              type="button"
              key={option.key}
              className={`cropper-preset ${option.key === aspectKey ? 'is-active' : ''}`}
              onClick={() => {
                setAspectKey(option.key);
                setOffset({ x: 0, y: 0 });
                setZoom(1);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div
          ref={viewportRef}
          className="cropper-viewport"
          style={{ aspectRatio }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          role="presentation"
        >
          <img
            src={image.previewSrc || image.src}
            alt={image.alt || image.name || 'Crop preview'}
            className="cropper-image"
            draggable="false"
            style={{
              width: `${renderedSize.width}px`,
              height: `${renderedSize.height}px`,
              left: `calc(50% + ${constrainedOffset.x}px)`,
              top: `calc(50% + ${constrainedOffset.y}px)`,
            }}
          />
          <div className="cropper-frame" aria-hidden="true" />
        </div>

        <label className="cropper-zoom-label" htmlFor="cropper-zoom">
          Zoom
        </label>
        <input
          id="cropper-zoom"
          type="range"
          min="1"
          max="3"
          step="0.01"
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
        />

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleConfirm}>
            Crop & Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropModal;
